import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How long does EV charging take?',
      answer: 'Charging time depends on your charger power and vehicle battery size. A 7 kW charger typically adds 35-45 km of range per hour. DC fast chargers (60-350 kW) can charge to 80% in 15-30 minutes.'
    },
    {
      question: 'Can I charge from a normal South African plug?',
      answer: 'Yes! Our portable 3 kW chargers work with standard SA 3-pin plugs. However, for faster charging, we recommend installing a dedicated circuit or wall-mounted charger.'
    },
    {
      question: 'What charger size do I need?',
      answer: 'For home use, a 7 kW charger is ideal for overnight charging. Commercial installations typically use 22 kW AC chargers or 60+ kW DC fast chargers depending on usage requirements.'
    },
    {
      question: 'Do you offer installation?',
      answer: 'Yes! We provide full installation services by certified technicians. This includes site assessment, electrical upgrades if needed, installation, testing, and commissioning.'
    },
    {
      question: 'Are your chargers compatible with all EV brands?',
      answer: 'Yes. We supply Type 2 (AC) and CCS2/CHAdeMO (DC) chargers which are compatible with all major EV brands sold in South Africa including Tesla, BMW, Audi, Nissan, Hyundai, and more.'
    },
    {
      question: 'Can chargers work with solar systems?',
      answer: 'Absolutely! Our smart chargers can be integrated with solar PV systems and configured to prioritize solar charging, helping you maximize clean energy use and reduce electricity costs.'
    },
    {
      question: 'Do commercial stations support payment systems?',
      answer: 'Yes. Our commercial charging stations support RFID access control, mobile app payments, and can integrate with billing systems for public or employee use.'
    },
    {
      question: 'What areas do you service?',
      answer: 'We service all major metropolitan areas in South Africa including Gauteng, Western Cape, and KwaZulu-Natal. Contact us to discuss installations in other provinces.'
    },
    {
      question: 'What happens during load shedding?',
      answer: 'During load shedding, charging will pause. Once power returns, most chargers automatically resume. We can also integrate chargers with backup power systems or schedule charging during off-peak hours.'
    },
    {
      question: 'What warranty and support do you provide?',
      answer: 'All our chargers come with manufacturer warranties (typically 2-3 years). We provide ongoing technical support, maintenance services, and emergency repairs to keep your charging infrastructure running smoothly.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <section
        data-testid="faq-hero-section"
        className="relative py-32 bg-gradient-to-br from-[#0A3D2E] to-[#07291F] text-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter mb-6 font-['Outfit']">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Everything you need to know about EV charging in South Africa.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="faq-content-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                data-testid={`faq-item-${faq.question.slice(0, 20)}`}
                className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(faq.question)}
                  data-testid={`faq-question-${faq.question.slice(0, 20)}`}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F4F7F5] transition-colors"
                >
                  <span className="text-lg font-semibold text-[#0A3D2E] pr-8 font-['Outfit']">
                    {faq.question}
                  </span>
                  <ChevronDown
                    size={24}
                    className={`text-[#0A3D2E] flex-shrink-0 transition-transform ${
                      openIndex === faq.question ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === faq.question && (
                  <div data-testid={`faq-answer-${faq.question.slice(0, 20)}`} className="px-6 pb-6">
                    <p className="text-base text-[#4A5D54] leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="faq-cta-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              Still Have Questions?
            </h2>
            <p className="text-lg text-[#4A5D54] mb-8">
              Our team is here to help. Get in touch with us today.
            </p>
            <a
              href="/contact"
              data-testid="faq-contact-button"
              className="inline-block px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
